import { HomeOutlined, NotificationOutlined, UserOutlined } from '@ant-design/icons';
import { Layout, Tabs, Tooltip, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { SEO_DEFAULTS, PAGE_SEO } from '../../utils/seoConfig';
import FullPageMediaViewer from '../MediaViewer/FullPageMediaViewer';
import FeedContainer from './FeedContainer';
import './PostsPage.css';

const { Content } = Layout;

// SEO configuration for each tab
const TAB_SEO = {
  home: {
    title: 'Posts - CellCog Community',
    description: 'Explore AI-generated content, research reports, marketing materials, and more created by the CellCog community. See what\'s possible with AI super agents.',
  },
  'product-updates': {
    title: 'Product Updates - CellCog',
    description: 'Stay updated with the latest features, improvements, and announcements from CellCog. Discover new AI capabilities and platform enhancements.',
  },
  'my-posts': {
    title: 'My Posts - CellCog',
    description: 'View and manage your AI-generated posts and content on CellCog.',
  },
};

const PostsPage = () => {
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // Default tab is Product Updates (Posts moved into the user dropdown as "Product Updates").
  // An explicit location.state.activeTab (e.g. a deep link to Home / My Posts) still wins.
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'product-updates');
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  // Get current tab SEO
  const currentSEO = TAB_SEO[activeTab] || TAB_SEO.home;

  // Check for intended tab on mount (after login redirect)
  useEffect(() => {
    if (currentUser) {
      const intendedTab = sessionStorage.getItem('posts_intended_tab');
      if (intendedTab) {
        setActiveTab(intendedTab);
        sessionStorage.removeItem('posts_intended_tab');
      }
    }
  }, [currentUser]);

  const handleTabChange = (key) => {
    if (key === 'my-posts' && !currentUser) {
      // Save intended destination
      saveIntendedUrl('/posts');
      // Store the desired tab in sessionStorage
      sessionStorage.setItem('posts_intended_tab', 'my-posts');
      message.info('Please login to view your posts');
      navigate('/login');
      return;
    }
    setActiveTab(key);
  };

  const handleMediaClick = (post, mediaIndex) => {
    // Now support all media types, not just images
    if (post && post.media_files && post.media_files.length > 0) {
      setSelectedMedia(post.media_files);
      setSelectedMediaIndex(mediaIndex);
      setViewerVisible(true);
    }
  };

  const handleCloseMediaViewer = () => {
    setViewerVisible(false);
    setSelectedMedia(null);
    setSelectedMediaIndex(0);
  };

  const tabItems = [
    {
      key: 'home',
      label: (
        <span className="tab-label">
          <HomeOutlined />
          Home
        </span>
      ),
      children: (
        <FeedContainer
          key="home-feed"
          activeTab="home"
          postType="chat"
          onMediaClick={handleMediaClick}
        />
      )
    },
    {
      key: 'product-updates',
      label: (
        <span className="tab-label">
          <NotificationOutlined />
          Product Updates
        </span>
      ),
      children: (
        <FeedContainer
          key="updates-feed"
          activeTab="product-updates"
          postType="major_update,minor_update"
          onMediaClick={handleMediaClick}
        />
      )
    },
    {
      key: 'my-posts',
      label: currentUser ? (
        <span className="tab-label">
          <UserOutlined />
          My Posts
        </span>
      ) : (
        <Tooltip title="Login to view your posts">
          <span className="tab-label tab-label--disabled">
            <UserOutlined />
            My Posts
          </span>
        </Tooltip>
      ),
      disabled: !currentUser,
      children: currentUser ? (
        <FeedContainer
          key="my-posts-feed"
          activeTab="my-posts"
          onMediaClick={handleMediaClick}
        />
      ) : null
    }
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{currentSEO.title} | {SEO_DEFAULTS.siteName}</title>
        <meta name="description" content={currentSEO.description} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/posts`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={currentSEO.title} />
        <meta property="og:description" content={currentSEO.description} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/posts`} />
        <meta property="og:image" content={SEO_DEFAULTS.image} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentSEO.title} />
        <meta name="twitter:description" content={currentSEO.description} />
        <meta name="twitter:image" content={SEO_DEFAULTS.image} />
        <meta name="twitter:site" content={SEO_DEFAULTS.twitterHandle} />
      </Helmet>

      <Layout className={`posts-page page-gradient-background ${isDarkMode ? 'dark' : 'light'}`}>
        <Content className="posts-content">
          <div className="posts-feed-wrapper">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={tabItems}
              className="posts-tabs"
              size="large"
              centered
            />
          </div>
        </Content>

        {/* Updated Media Viewer - now supports all media types */}
        {viewerVisible && selectedMedia && selectedMedia.length > 0 && (
          <FullPageMediaViewer
            mediaFiles={selectedMedia}
            initialIndex={selectedMediaIndex}
            onClose={handleCloseMediaViewer}
          />
        )}
      </Layout>
    </>
  );
};

export default PostsPage;
